"use client";
import { useState } from "react";
import { twMerge } from "tailwind-merge";
import { Button } from "../../Button";
import { ControlBase } from "./primitives/ControlBase";
import { ControlButton } from "./primitives/ControlButton";
import type { ControlPosition } from "./primitives/utils";

interface MapSearchControlProps {
  position?: ControlPosition;
  className?: string;
  /**
   * Custom positioning - overrides position prop
   * Example: { top: '20px', left: '20px' }
   */
  style?: React.CSSProperties;
  /**
   * Callback when search is triggered
   */
  onSearch?: (query: string) => void;
  /**
   * Placeholder text for search input
   * @default "Search places..."
   */
  placeholder?: string;
  /**
   * Show search input field
   * @default true
   */
  showSearchInput?: boolean;
}

export function SearchControl({
  position = "top-left",
  className,
  style,
  onSearch,
  placeholder = "Search places...",
  showSearchInput = true,
}: MapSearchControlProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const handleSearchClick = () => {
    if (showSearchInput) {
      setIsOpen(!isOpen);
    } else if (onSearch) {
      onSearch("");
    }
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (onSearch && searchQuery.trim()) {
      onSearch(searchQuery.trim());
      setIsOpen(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      setIsOpen(false);
    }
  };

  return (
    <ControlBase position={position} className={className} style={style}>
      <div className="relative">
        <ControlButton icon="MagnifyingGlass" onPress={handleSearchClick} ariaLabel="Search" />

        {/* Search Input Panel */}
        {isOpen && showSearchInput && (
          <div
            className={twMerge(
              "absolute top-12 left-0 min-w-64",
              "bg-background-surface border border-border rounded-lg shadow-xl p-3"
            )}
          >
            <form onSubmit={handleSearchSubmit}>
              <div className="flex items-center space-x-2">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={placeholder}
                  className={twMerge(
                    "flex-1 px-3 py-2 text-sm rounded border",
                    "bg-background border-border text-text-primary placeholder:text-text-secondary",
                    "focus:outline-none focus:ring-2 focus:ring-action-brand"
                  )}
                  autoFocus
                />
                <Button type="submit" variant="primary" size="sm" className="px-3 py-2">
                  Search
                </Button>
              </div>
            </form>
          </div>
        )}
      </div>
    </ControlBase>
  );
}
